import api, { clearCache } from './api';

export async function fetchUserLists(options = {}) {
  const { data } = await api.get('/api/user/lists', { skipCache: options.skipCache || false });
  return data.lists || [];
}

export async function fetchListDetail(listId, params = {}, options = {}) {
  const p = { ...params };
  if (options.skipCache) p._t = Date.now();
  const { data } = await api.get(`/api/user/lists/${listId}`, { params: p, skipCache: options.skipCache || false });
  return data;
}

export async function createList({ name, description, isPrivate, isSmart, rules }) {
  const { data } = await api.post('/api/user/lists', { name, description, isPrivate, isSmart, rules });
  clearCache('/api/user/lists');
  return data.list;
}

export async function smartListPreview({ match, filters }) {
  const { data } = await api.post('/api/user/lists/smart-preview', { match, filters });
  return data.count ?? 0;
}

export async function updateList(listId, { name, description, isPrivate }) {
  const { data } = await api.patch(`/api/user/lists/${listId}`, { name, description, isPrivate });
  clearCache('/api/user/lists');
  return data.list;
}

export async function deleteList(listId) {
  const { data } = await api.delete(`/api/user/lists/${listId}`);
  clearCache('/api/user/lists');
  return data;
}

export async function addProblemsToList(listId, problemIds) {
  const { data } = await api.post(`/api/user/lists/${listId}/problems`, { problemIds });
  clearCache('/api/user/lists');
  return data;
}

export async function removeProblemFromList(listId, problemId) {
  const { data } = await api.delete(`/api/user/lists/${listId}/problems/${problemId}`);
  clearCache('/api/user/lists');
  return data;
}

export async function searchProblemsForModal({ page = 1, limit = 20, search = '', difficulty = '', listId }) {
  const params = { page, limit };
  if (search.trim()) params.search = search.trim();
  if (difficulty) params.difficulty = difficulty;
  if (listId) params.listId = listId;
  const { data } = await api.get('/api/problems/search', { params });
  return {
    problems: data.problems || [],
    pagination: data.pagination || { currentPage: 1, totalPages: 1, totalProblems: 0, limit: 20 }
  };
}

/**
 * Shared utility function to toggle favorite status for a problem.
 * Handles optimistic updates and error rollback.
 * @param {string} favoriteListId - The ID of the favorite list
 * @param {string} problemId - The ID of the problem to toggle
 * @param {boolean} currentlyInFavorite - Current favorite status
 * @param {Function} updateProblem - Function to update problem in state: (prev) => updatedState
 * @param {string} favoriteField - Field name for favorite status ('inFavorite' or 'isInFavorite')
 * @returns {Promise<boolean>} New favorite status, or original status on error
 */
export async function toggleFavoriteProblem(favoriteListId, problemId, currentlyInFavorite, updateProblem, favoriteField = 'isInFavorite') {
  if (!favoriteListId) return currentlyInFavorite;
  
  const nextFavorite = !currentlyInFavorite;
  
  // Optimistic update
  updateProblem(prev => {
    if (Array.isArray(prev)) {
      // For array of problems
      return prev.map(p => {
        const id = (p._id || p.id || p.id)?.toString();
        if (id && id === String(problemId)) {
          return { ...p, [favoriteField]: nextFavorite };
        }
        return p;
      });
    } else if (prev && prev.problems) {
      // For object with problems array
      return {
        ...prev,
        problems: (prev.problems || []).map(p => {
          const id = (p._id || p.id || p.id)?.toString();
          if (id && id === String(problemId)) {
            return { ...p, [favoriteField]: nextFavorite };
          }
          return p;
        })
      };
    }
    return prev;
  });
  
  try {
    if (nextFavorite) {
      await addProblemsToList(favoriteListId, [problemId]);
    } else {
      await removeProblemFromList(favoriteListId, problemId);
    }
    return nextFavorite;
  } catch (e) {
    // Rollback on error
    updateProblem(prev => {
      if (Array.isArray(prev)) {
        return prev.map(p => {
          const id = (p._id || p.id || p.id)?.toString();
          if (id && id === String(problemId)) {
            return { ...p, [favoriteField]: currentlyInFavorite };
          }
          return p;
        });
      } else if (prev && prev.problems) {
        return {
          ...prev,
          problems: (prev.problems || []).map(p => {
            const id = (p._id || p.id || p.id)?.toString();
            if (id && id === String(problemId)) {
              return { ...p, [favoriteField]: currentlyInFavorite };
            }
            return p;
          })
        };
      }
      return prev;
    });
    
    if (window.toast) {
      window.toast.error(nextFavorite ? 'Failed to add to Favorites' : 'Failed to remove from Favorites');
    }
    return currentlyInFavorite;
  }
}
